import {useMemo, useRef} from "react";
import {useLocale, useMessages} from "i18n";
import {SheetTitleAccent} from "components/ModalSheet";
import {exitOfferAmountsFor} from "upsell/exitOffer";
import {ladderDiscount, ladderPresentationFor} from "offers/ladder/ladderOffer";
import {withLadderFirstMonth} from "offers/ladder/ladderPlanNotes";
import {LadderIntroOffer} from "offers/ladder/LadderIntroOffer";
import {LadderOfferNotice} from "offers/ladder/LadderOfferNotice";
import type {PricingPageState} from "./usePricingPage";
import {ReferralOfferDialog} from "referral/ReferralOfferDialog";
import {BillingPeriod, PlanId} from "./pricingPlans";
import {PricingContent} from "./PricingContent";
import {FREE_PLAN_ANCHOR_ID} from "./pricingComparison";
import {BackToTopButton} from "./BackToTopButton";
import type {OfferTokenSource} from "./offerTokenSource";
import {Eyebrow, Header, Lede, Title, Wrap} from "./PricingPage.styles";

/**
 * v3 public pricing — rebuilt to the design's `.pricing-modal` surface (`Transferify.dc.html`):
 * a centered header (mono eyebrow + Instrument-Serif title + lede) over the shared
 * {@link PricingContent} body (billing toggle, Free/Basic/Plus/Premium grid, compare table). Since
 * ticket #45 the same body also renders inside {@link PricingDialog} from the landing topbar;
 * this page remains the SSG/SEO surface for `/pricing` deep links.
 *
 * All state and pricing logic lives in `usePricingPage` (epic #26 mandate); this component is a dumb
 * view that forwards each card's CTA to {@link PricingPageProps.onUpgrade}. The view-model arrives as
 * a PROP rather than being built here: the route hosts the checkout dialog and has to send the same
 * decided ladder answer this page renders from, and a second call of the hook would be a second read
 * of the welcome price — a second counter request, and a second showing reported for one visit.
 *
 * The header keeps `pricingLargeFilesGuide`, the in-flow link to the head-term landing page (ui#1800);
 * the marketing lede that preceded it was removed by ui#2426. It is rendered HERE rather than inside
 * {@link PricingContent} on purpose: the shared body
 * also paints the pricing dialog, and this route is the SSG surface whose authority the link exists to
 * pass on. Keeping it in the page's own header is also what keeps a link out of the dialog, which has
 * nowhere to navigate to without losing the reader mid-conversion.
 */

type PricingPageProps = {
	/**
	 * The decided view-model, built by the route so the checkout dialog beside this page reads the same
	 * answers — the same welcome price and the same ladder rung — that these cards render from.
	 */
	page: PricingPageState;
	/** Card CTA: the tier, the billing period and the welcome claim token's resolver (see {@link usePricingUpgrade}). */
	onUpgrade: (planId: PlanId, period: BillingPeriod, offerToken?: OfferTokenSource) => void;
	/**
	 * The ladder link's own two controls, for the intro rung's panel: the credential it pays with, and
	 * the way back to list prices when the till refuses it (kitecorp/transferify-ui#1977). Absent on
	 * every ordinary visit.
	 *
	 * What the link is WORTH is not here: it reaches this page through {@link page} alone
	 * (`ladderBeside`), already weighed against the welcome price, so no surface can read the link's
	 * raw resolution and state an offer the rest of the page has set aside.
	 *
	 * A rung that carried the referred friend's GIFT instead of a discount (kitecorp/transferify-ui#2033)
	 * states nothing here at all: it raises the picker below and leaves the strip, the card notes and the
	 * checkout token alone, because the gift is a different purchase at a different price.
	 */
	ladder?: {token: string | null; markExpired: () => void};
};

export default function PricingPage({page, onUpgrade, ladder}: PricingPageProps) {
	const {pricing, pricingLargeFilesGuide, pricingComparisons, offerLadder} = useMessages();
	const {locale} = useLocale();
	const title = useRef<HTMLHeadingElement>(null);
	const {period, plans, comparisonRows, storefront, referral, referralClaim, pressPlanCta,
		referralIntro, claimableTiers, welcome, welcomeOccasion, ladderBeside} = page;
	// The day-14 rung's figures, in the currency THIS reader's checkout will charge. Resolved before the
	// presentation, because whether this storefront can price the intro Price is part of deciding what
	// the page may say at all.
	const introAmounts = useMemo(() => exitOfferAmountsFor(storefront), [storefront]);
	// The rung is set aside while the welcome price is live (`ladderBeside`), so the strip, the notes
	// and the route's checkout token all follow the card's figure.
	//
	// This is the one place that narrows it further, and only ever downgrades the day-14 `intro` rung to
	// the ordinary page on a storefront this bundle cannot price it in. The route's token is the
	// un-narrowed answer deliberately: the link is alive and the api prices that rung from its own edge
	// country, so a checkout must still be allowed to spend it.
	const presentation = ladderPresentationFor(ladderBeside, introAmounts !== null);
	// The rung's discounted first invoice, written onto the monthly cards by a React-free rule.
	//
	// `claimableTiers` is what keeps the two offers on this page from contradicting each other: the
	// referred friend's gift SWAPS those cards' CTA to open the picker at its own intro price, so the
	// ladder must leave their note alone rather than quote a first invoice the press would not charge.
	const ladderPlans = useMemo(
		() => withLadderFirstMonth(plans, period, storefront.pricing, ladderDiscount(presentation),
			locale, offerLadder.firstMonths, claimableTiers),
		[plans, period, storefront, presentation, locale, offerLadder.firstMonths, claimableTiers],
	);
	// The paid-moment funnel is told which card was pressed before the verdict runs (ui#1965), exactly
	// as the pricing dialog tells it, so a checkout reached from this page carries the picker's own
	// figure. `pressPlanCta` also owns the referral swap: a card this reader can CLAIM opens the
	// friend's gift instead of the ordinary checkout, and that decision is the view-model's rather than
	// each host's.
	//
	// THAT FIGURE IS THE LIST PRICE, and a ladder rung's discounted line beside it does not change it
	// (kitecorp/transferify-ui#1977). `PLAN_UPGRADE` measures the plan picker, whose price is the same
	// for every reader; the emailed rung is its OWN surface, counted server-side because only the api
	// can witness it. It is also the figure the money agrees with: `ExitOfferConversionValue` values a
	// ladder sale at one REGULAR month rather than at the discounted first invoice, because a
	// discounted first month is still a month.
	const pressCta = (planId: PlanId, period: BillingPeriod) =>
		pressPlanCta(planId, period, offerToken => onUpgrade(planId, period, offerToken));

	return (
		<Wrap>
			<Header>
				<Eyebrow>{pricing.eyebrow}</Eyebrow>
				<Title ref={title} tabIndex={-1}>{pricing.titleLead}<SheetTitleAccent>{pricing.titleAccent}</SheetTitleAccent></Title>
				<Lede>{pricingLargeFilesGuide()}</Lede>
				{pricingComparisons ? <Lede>{pricingComparisons()}</Lede> : null}
			</Header>
			<LadderOfferNotice presentation={presentation}/>
			{presentation.kind === "intro" && introAmounts !== null && ladder?.token != null && (
				<LadderIntroOffer
					amounts={introAmounts}
					token={ladder.token}
					onOfferExpired={ladder.markExpired}
				/>
			)}
			<PricingContent
				period={period}
				plans={ladderPlans}
				welcome={welcome}
				welcomeOccasion={welcomeOccasion}
				planHeadingLevel={2}
				comparisonRows={comparisonRows}
				onUpgrade={pressCta}
				referralIntro={referralIntro}
				freePlanAnchorId={FREE_PLAN_ANCHOR_ID}
			/>
			{/* The friend's gift, opened by the swapped CTA on the tiers this reader may claim (ui#1993). */}
			{referral.model !== null && (
				<ReferralOfferDialog
					open={referral.open}
					model={referral.model}
					onClose={referral.close}
					onPaid={referral.closeAfterPaid}
					claim={referralClaim}
				/>
			)}
			<BackToTopButton focusTarget={title}/>
		</Wrap>
	);
}
